import { useEffect, useState, useCallback } from "react";
import { adminApi, BACKEND_URL, formatAud } from "../../lib/api";
import { toast } from "sonner";
import {
  Search, FileText, Printer, Mail, Star, Pencil,
  X, Plus, Trash2, RefreshCcw, Check,
} from "lucide-react";
import { Input }  from "../../components/ui/input";
import { Label }  from "../../components/ui/label";

const PAY_BADGE = {
  stripe:       "bg-purple-50 text-purple-700 border-purple-200",
  card:         "bg-blue-50 text-blue-700 border-blue-200",
  cash:         "bg-green-50 text-green-700 border-green-200",
  bank_transfer:"bg-amber-50 text-amber-700 border-amber-200",
  walkin:       "bg-zinc-50 text-zinc-600 border-zinc-200",
};

const TYPE_BADGE = {
  order:  "bg-blue-50 text-blue-700 border-blue-200",
  walkin: "bg-zinc-50 text-zinc-600 border-zinc-200",
};

const TIME_FILTERS = [
  { key: "today",      label: "Today"      },
  { key: "this_week",  label: "This Week"  },
  { key: "this_month", label: "This Month" },
  { key: "all",        label: "All"        },
];

function getDateRange(tf) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch(tf) {
    case "today":      return [today.toISOString(), null];
    case "this_week": {
      const dow = today.getDay();
      const mon = new Date(today); mon.setDate(mon.getDate() - (dow===0?6:dow-1));
      return [mon.toISOString(), null];
    }
    case "this_month": {
      const fm = new Date(now.getFullYear(), now.getMonth(), 1);
      return [fm.toISOString(), null];
    }
    default: return [null, null];
  }
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU", { day:"numeric", month:"short", year:"numeric" }) : "—";

// ─── Edit Note Modal ────────────────────────────────────────────────────────
function EditNoteModal({ invoice, onClose, onSaved }) {
  const [note, setNote] = useState(invoice.seller_note || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(`/admin/invoices/${invoice.id}/note`, { note });
      toast.success("Note updated");
      onSaved(note);
      onClose();
    } catch { toast.error("Failed to update note"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(0,0,0,0.45)" }}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" style={{ border:"1px solid #dde3ef" }}>
        <div className="p-5 flex items-center justify-between" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div>
            <div className="font-heading font-bold text-[16px]" style={{ color:"#0f172a" }}>Edit Seller Note</div>
            <div className="text-[11px] font-mono" style={{ color:"#94a3b8" }}>{invoice.invoice_number}</div>
          </div>
          <button onClick={onClose} style={{ color:"#94a3b8" }}><X className="h-5 w-5"/></button>
        </div>
        <div className="p-5 space-y-3">
          <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>
            Seller Note (prints on invoice)
          </Label>
          <textarea value={note} onChange={e=>setNote(e.target.value)} rows={4}
            placeholder="Warranty info, condition notes, installation instructions..."
            className="w-full rounded-xl border px-3 py-2 text-[13px] resize-none outline-none"
            style={{ borderColor:"#dde3ef", color:"#0f172a" }}/>
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving}
              className="flex-1 rounded-xl py-2 font-bold text-[13px] flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ background:"#0b1a3b", color:"#ffffff" }}>
              {saving ? "Saving…" : <><Check className="h-4 w-4"/>Save Note</>}
            </button>
            <button onClick={onClose}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold"
              style={{ background:"#f5f7fb", border:"1px solid #dde3ef", color:"#475569" }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Walk-in Invoice Modal (unchanged from original) ──────────────────────
const PAYMENT_METHODS = ["cash", "card", "bank_transfer"];

function WalkInModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", customer_address: "",
    payment_method: "cash", sales_person: "", notes: "",
  });
  const [items, setItems] = useState([{ description: "", sku: "", qty: 1, unit_price: 0 }]);
  const [busy, setBusy] = useState(false);

  const updateItem = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: field==="qty"||field==="unit_price" ? Number(val) : val };
    setItems(next);
  };
  const addItem    = () => setItems([...items, { description:"", sku:"", qty:1, unit_price:0 }]);
  const removeItem = (i) => setItems(items.filter((_,idx)=>idx!==i));

  const subtotal = items.reduce((s,it)=>s+(it.unit_price*it.qty),0);
  const gst      = Math.round(subtotal/11*100)/100;

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Customer name required"); return; }
    if (items.some(it=>!it.description.trim())) { toast.error("All items need a description"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/invoices/walkin", { ...form, items });
      toast.success(`Invoice ${data.invoice_number} created`);
      onCreated(data);
      onClose();
    } catch(e) { toast.error(e?.response?.data?.detail||"Failed to create invoice"); }
    finally { setBusy(false); }
  };

  const inp = { background:"#fff", borderColor:"#dde3ef", color:"#0f172a" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background:"rgba(0,0,0,0.45)" }}
      onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        style={{ border:"1px solid #dde3ef" }} onClick={e=>e.stopPropagation()}>
        <div className="p-5 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl" style={{ borderBottom:"1px solid #eef2f8" }}>
          <div className="font-heading font-bold text-[18px]" style={{ color:"#0f172a" }}>New Walk-in Invoice</div>
          <button onClick={onClose}><X className="h-5 w-5" style={{ color:"#94a3b8" }}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[["customer_name","Customer Name *"],["customer_phone","Phone"],["customer_email","Email"],["customer_address","Address"]].map(([k,l])=>(
              <div key={k}>
                <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color:"#64748b" }}>{l}</Label>
                <Input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="rounded-xl" style={inp}/>
              </div>
            ))}
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color:"#64748b" }}>Payment Method</Label>
              <select value={form.payment_method} onChange={e=>setForm({...form,payment_method:e.target.value})}
                className="w-full rounded-xl border px-3 py-2 text-[13px]" style={inp}>
                {PAYMENT_METHODS.map(m=><option key={m} value={m}>{m.replace("_"," ")}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color:"#64748b" }}>Sales Person</Label>
              <Input value={form.sales_person} onChange={e=>setForm({...form,sales_person:e.target.value})} placeholder="Sam" className="rounded-xl" style={inp}/>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] font-mono uppercase tracking-wide" style={{ color:"#64748b" }}>Items</Label>
              <button onClick={addItem} className="text-[11px] font-semibold flex items-center gap-1" style={{ color:"#1e5bb8" }}>
                <Plus className="h-3.5 w-3.5"/> Add item
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    {i===0&&<Label className="text-[9px] font-mono uppercase tracking-wide mb-0.5 block" style={{ color:"#94a3b8" }}>Description *</Label>}
                    <Input value={it.description} onChange={e=>updateItem(i,"description",e.target.value)} placeholder="Part description" className="rounded-xl" style={inp}/>
                  </div>
                  <div className="col-span-2">
                    {i===0&&<Label className="text-[9px] font-mono uppercase tracking-wide mb-0.5 block" style={{ color:"#94a3b8" }}>SKU</Label>}
                    <Input value={it.sku} onChange={e=>updateItem(i,"sku",e.target.value)} placeholder="SKU" className="rounded-xl" style={inp}/>
                  </div>
                  <div className="col-span-2">
                    {i===0&&<Label className="text-[9px] font-mono uppercase tracking-wide mb-0.5 block" style={{ color:"#94a3b8" }}>Qty</Label>}
                    <Input type="number" min={1} value={it.qty} onChange={e=>updateItem(i,"qty",e.target.value)} className="rounded-xl" style={inp}/>
                  </div>
                  <div className="col-span-2">
                    {i===0&&<Label className="text-[9px] font-mono uppercase tracking-wide mb-0.5 block" style={{ color:"#94a3b8" }}>Price</Label>}
                    <Input type="number" min={0} step={0.01} value={it.unit_price} onChange={e=>updateItem(i,"unit_price",e.target.value)} className="rounded-xl" style={inp}/>
                  </div>
                  <div className="col-span-1 flex items-end justify-center pb-1">
                    <button onClick={()=>removeItem(i)} disabled={items.length===1}
                      className="text-red-400 hover:text-red-600 disabled:opacity-30">
                      <Trash2 className="h-4 w-4"/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[11px] font-mono uppercase tracking-wide mb-1 block" style={{ color:"#64748b" }}>Notes</Label>
            <textarea rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}
              placeholder="Warranty info, condition notes..." className="w-full rounded-xl border px-3 py-2 text-[13px] resize-none outline-none" style={inp}/>
          </div>

          <div className="flex justify-between items-center pt-2" style={{ borderTop:"1px solid #eef2f8" }}>
            <div className="text-[13px]" style={{ color:"#64748b" }}>
              Subtotal: <strong style={{ color:"#0f172a" }}>{formatAud(subtotal)}</strong>
              <span className="ml-3">GST included: <strong style={{ color:"#0f172a" }}>{formatAud(gst)}</strong></span>
            </div>
            <button onClick={submit} disabled={busy}
              className="rounded-xl font-bold px-6 py-2 text-[13px] flex items-center gap-1.5 disabled:opacity-50"
              style={{ background:"#0b1a3b", color:"#ffffff" }}>
              {busy ? "Creating…" : "Create Invoice"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main AdminInvoices page ────────────────────────────────────────────────
export default function AdminInvoices() {
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState("");
  const [timeFilter, setTimeFilter] = useState("all");
  const [showWalkin, setShowWalkin] = useState(false);
  const [editInv,    setEditInv]    = useState(null);   // invoice being edited
  const [sending,    setSending]    = useState({});     // {id: true} loading map

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [df, dt] = getDateRange(timeFilter);
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (df) params.set("date_from", df);
      if (dt) params.set("date_to",   dt);
      const { data } = await adminApi.get(`/admin/invoices?${params}`);
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, [search, timeFilter]);

  useEffect(() => { load(); }, [load]);

  const viewInvoice = async (inv) => {
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Popup blocked — please allow popups for this site in your browser settings");
      return;
    }
    win.document.write("<html><body style='font-family:Arial;padding:40px;color:#555'>Loading invoice...</body></html>");
    try {
      const token      = localStorage.getItem("bcp_admin_token");
      const backendUrl = BACKEND_URL || "http://localhost:8001";
      const url = inv.type === "walkin"
        ? `${backendUrl}/api/admin/invoices/walkin/${inv.id}/html`
        : `${backendUrl}/api/admin/orders/${inv.id}/invoice`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();
      win.document.write(html);
      win.document.close();
    } catch(e) {
      win.document.body.innerHTML = "<div style='font-family:Arial;padding:40px;color:red'>Failed to load invoice: " + e.message + "</div>";
    }
  };

  const emailInvoice = async (inv) => {
    setSending(s => ({ ...s, [inv.id]: true }));
    try {
      if (inv.type === "walkin") {
        toast.info("Email for walk-in invoices — coming soon");
      } else {
        await adminApi.post(`/admin/orders/${inv.id}/send-invoice`);
        toast.success(`Invoice emailed to ${inv.customer_email}`);
      }
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setSending(s => ({ ...s, [inv.id]: false })); }
  };

  const requestReview = async (inv) => {
    if (!inv.order_number) { toast.info("Review requests only available for online orders"); return; }
    setSending(s => ({ ...s, [`rev_${inv.id}`]: true }));
    try {
      await adminApi.post(`/admin/orders/${inv.id}/request-review`);
      toast.success(`Review request sent to ${inv.customer_email}`);
    } catch(e) { toast.error(e?.response?.data?.detail || "Failed to send review request"); }
    finally { setSending(s => ({ ...s, [`rev_${inv.id}`]: false })); }
  };

  const totalRevenue = items.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="admin-invoices-page">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-1" style={{ color:"#94a3b8" }}>Finance</div>
          <h1 className="font-heading text-[26px] font-bold" style={{ color:"#0f172a" }}>Invoices</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[12px]" style={{ color:"#64748b" }}>{items.length} invoice{items.length!==1?"s":""}</span>
            {items.length > 0 && (
              <span className="text-[12px] font-semibold" style={{ color:"#16a34a" }}>{formatAud(totalRevenue)} total</span>
            )}
          </div>
        </div>
        <button onClick={() => setShowWalkin(true)}
          className="h-9 px-4 rounded-xl font-semibold text-[13px] flex items-center gap-1.5 transition-all hover:opacity-90"
          style={{ background:"#0b1a3b", color:"#ffffff" }}>
          <Plus className="h-4 w-4"/> New Walk-in Invoice
        </button>
      </div>

      {/* Time filters */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {TIME_FILTERS.map(({ key, label }) => (
          <button key={key} onClick={() => setTimeFilter(key)}
            className="px-3 h-8 rounded-xl text-[11px] font-mono font-semibold uppercase tracking-wide transition-all"
            style={timeFilter===key
              ? { background:"#0b1a3b", color:"#ffffff" }
              : { background:"#f5f7fb", border:"1px solid #dde3ef", color:"#64748b" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color:"#94a3b8" }}/>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Invoice #, order #, customer name or email…"
          className="w-full pl-9 pr-9 h-9 rounded-xl text-[12px] outline-none"
          style={{ background:"#ffffff", border:"1px solid #dde3ef", color:"#0f172a" }}/>
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color:"#94a3b8" }}>
            <X className="h-3 w-3"/>
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm" style={{ border:"1px solid #dde3ef" }}>
        <table className="w-full" style={{ borderCollapse:"collapse" }}>
          <thead>
            <tr style={{ background:"#f5f7fb", borderBottom:"1px solid #dde3ef" }}>
              {[
                ["Invoice #", "left", 110],
                ["Date",      "left",  95],
                ["Customer",  "left", 160],
                ["Order #",   "left", 110],
                ["Type",      "center", 70],
                ["Amount",    "right",  80],
                ["Actions",   "right", 180],
              ].map(([h, align, w]) => (
                <th key={h} className="py-2.5 px-3 font-mono font-semibold uppercase"
                  style={{ color:"#94a3b8", fontSize:10, letterSpacing:"0.12em", textAlign:align, width:w }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-10 text-center">
                <div className="w-5 h-5 border-2 border-[#1e5bb8] border-t-transparent rounded-full animate-spin mx-auto"/>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center font-mono uppercase tracking-widest text-[11px]" style={{ color:"#94a3b8" }}>
                No invoices found
              </td></tr>
            ) : items.map((inv, idx) => (
              <tr key={inv.id} style={{ borderBottom: idx < items.length-1 ? "1px solid #f0f4f8" : "none", background: idx%2===0?"#fff":"#fafbfc" }}>

                {/* Invoice # */}
                <td className="px-3 py-2.5">
                  <div className="font-heading font-bold text-[13px]" style={{ color:"#0b1a3b" }}>{inv.invoice_number}</div>
                </td>

                {/* Date */}
                <td className="px-3 py-2.5">
                  <div className="text-[12px] font-mono" style={{ color:"#64748b" }}>{fmtDate(inv.date)}</div>
                </td>

                {/* Customer */}
                <td className="px-3 py-2.5">
                  <div className="text-[12px] font-medium truncate" style={{ color:"#0f172a", maxWidth:155 }}>{inv.customer_name || "—"}</div>
                  <div className="text-[10px] font-mono truncate" style={{ color:"#94a3b8", maxWidth:155 }}>{inv.customer_email || ""}</div>
                </td>

                {/* Order # */}
                <td className="px-3 py-2.5">
                  <div className="text-[11px] font-mono" style={{ color:"#64748b" }}>{inv.order_number || "—"}</div>
                </td>

                {/* Type */}
                <td className="px-3 py-2.5 text-center">
                  <span className={`text-[9px] font-mono font-bold uppercase px-1.5 py-0.5 rounded-md border ${TYPE_BADGE[inv.type]||""}`}>
                    {inv.type === "walkin" ? "Walk-in" : "Online"}
                  </span>
                </td>

                {/* Amount */}
                <td className="px-3 py-2.5 text-right">
                  <div className="font-heading font-bold text-[13px]" style={{ color:"#0f172a" }}>{formatAud(inv.total)}</div>
                </td>

                {/* Actions */}
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {/* View & Print */}
                    <button onClick={() => viewInvoice(inv)} title="View & Print Invoice"
                      className="h-7 w-7 rounded-lg flex items-center justify-center transition-all hover:shadow-sm"
                      style={{ background:"#eff6ff", color:"#1e40af", border:"1px solid #93c5fd" }}>
                      <Printer className="h-3.5 w-3.5"/>
                    </button>

                    {/* Email */}
                    <button onClick={() => emailInvoice(inv)} title="Email Invoice to Customer"
                      disabled={sending[inv.id]}
                      className="h-7 w-7 rounded-lg flex items-center justify-center transition-all hover:shadow-sm disabled:opacity-40"
                      style={{ background:"#f0fdf4", color:"#15803d", border:"1px solid #86efac" }}>
                      <Mail className="h-3.5 w-3.5"/>
                    </button>

                    {/* Request Review — only for online orders with an email */}
                    {inv.type === "order" && inv.customer_email && (
                      <button onClick={() => requestReview(inv)} title="Send Google Review Request"
                        disabled={sending[`rev_${inv.id}`]}
                        className="h-7 w-7 rounded-lg flex items-center justify-center transition-all hover:shadow-sm disabled:opacity-40"
                        style={{ background:"#fffbeb", color:"#d97706", border:"1px solid #fde68a" }}>
                        <Star className="h-3.5 w-3.5"/>
                      </button>
                    )}

                    {/* Edit Note */}
                    <button onClick={() => setEditInv(inv)} title="Edit Seller Note"
                      className="h-7 w-7 rounded-lg flex items-center justify-center transition-all hover:shadow-sm"
                      style={{ background:"#f5f7fb", color:"#64748b", border:"1px solid #dde3ef" }}>
                      <Pencil className="h-3.5 w-3.5"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Walk-in modal */}
      {showWalkin && (
        <WalkInModal onClose={() => setShowWalkin(false)} onCreated={() => load()} />
      )}

      {/* Edit note modal */}
      {editInv && (
        <EditNoteModal
          invoice={editInv}
          onClose={() => setEditInv(null)}
          onSaved={(note) => setItems(items.map(i => i.id === editInv.id ? {...i, seller_note: note} : i))}
        />
      )}
    </div>
  );
}